import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { Area, AreasTags, Option, Tag } from "../../engine/models/models";
import { AreasService } from "../../services/areas.service";
import { Globals } from "../../services/globals";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "../../../environments/environment";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from '@angular/platform-browser';
import { Utilities } from "../../helpers/utilities";
import { GalleryItem, ImageItem } from 'ng-gallery';

export enum OptionStates {
  IsOptionalButNotSelected = 0,
  IsOptionalAndIsSelected = 1,
  IsIncludedButNotSelected = 2,
  IsIncludedAndIsSelected = 3,
  IsIncludedCustomButNotSelected = 4,
  IsIncludedCustomAndIsSelected = 6,
  IsOptionalCustomButNotSelected = 7,
  IsOptionalCustomAndIsSelected = 8
};

@Component({
  selector: 'option-selection',
  templateUrl: './option-selection.component.html',
  styleUrls: ['./option-selection.component.css']
})

export class OptionSelectionComponent {
  // The button color is controlled by the current collection, e.g.
  // Elements, Integrity, Traditions, Tribute, etc.
  @Input() collectionId = 0;
  get requiresStyleSelections(): boolean {
    //If an option is required but no option/subOption is selected
    if ((this.option.isRequired || this.option.isAdded) && !this.option.selectedOption) {
      return true;
    }

    if (!this.option?.selectedOption?.styleSelections) { return false; }

    const selectionsNeeded = this.option.selectedOption.styleSelections.length;
    let selectionsMade = 0;
    if (this.option.selectedOption.styleSelections) {
      for (const style of this.option.selectedOption.styleSelections) {
        for (const choice of style.styleChoices) {
          if (choice.isSelected) { selectionsMade++; }
        }
      }
    }

    return selectionsNeeded > selectionsMade;
  }

  get getOptionDescription(): string {
    if (this.option?.selectedOption) {
      if (this.option?.selectedOption.displayName) {
        return this.option?.selectedOption.displayName;
      }
      return this.option?.selectedOption.description;
    }

    if (this.option && this.option.displayName) {
      return this.option.displayName;
    }

    if (this.option && this.option.description) {
      return this.option.description;
    }
    return "Unknown Option";
  }

  get getLowestPrice(): number {
    if (this.option?.childOptions?.length) {
      const price = this.option.childOptions.map(q => q.price).reduce((a, b) => Math.min(a, b));
      return price;
    }
    return this.option.price;
  }

  get hasIncludedFeatures(): boolean {
    if (this.option?.selectedOption?.price === 0) return true;
    if (this.option?.childOptions?.length > 0) {
      if (this.option.childOptions.filter(q => q.price === 0).length > 0) { return true; }
    }
    return false;
  }

  get getHeaderDescription(): string {
    const option = this.option;

    var value = option.description ?? "[Missing Desc!]";

    if (option.cardDisplayName) { value = option.cardDisplayName; }

    if (option.description.startsWith(this.tag.name)) {
      value = option.description.replace(this.tag.name, "");

      if (value.indexOf(" - ") === 0) {
        value = value.substring(2, value.length - 3)
      }

      value = value.replace(" - ", "");
    }

    if (option.quantity > 1) {
      value = `${value} x${option.quantity}`;
    }

    return value;
  }

  @Input() option: Option;
  @Input() tag: Tag;
  @Input() selectedTag: Tag;
  @Input() filteredAreas: Area[] = [];
  @Input() selectedArea: Area;
  @Input() includeOptions: any;
  @Input() isLiked: boolean;
  @Input() isRequired: boolean;
  @Input() selectedOption: Option;
  @Input() subDescription: string;
  @Input() isOptionSelected: boolean;
  @Input() isStyleSelected: boolean;
  @Input() nextArea: Area;
  @Input() utilities: Utilities;
  @Input() isListView = false;
  @Input() isSideBar = false;
  @Input() displayedTags: Tag[] = [];
  @Input() areasTags: AreasTags;
  @Input() notSelectedImageUrl = "/assets/images/needsSelection.png";
  @Input() optionUpdateSubscription: any;
  @Input() previousArea: Area;
  @Input() placeholder = false;
  @Input() styleSelections?: Option;
  @Input() childOptions: Option;
  @Output() optionsSelected = new EventEmitter<Option>();
  private _gallery: string[] = [];
  optionStates = OptionStates;

  get galleryImages(): string[] {
    if (this.option.selectedPictureGallery?.length === 1 && !this.option.selectedPictureGallery[0]) {
      this.option.selectedPictureGallery = [];
    }

    let uniquePictures = Array.from(new Set(this.option.selectedPictureGallery)).filter(q => q != "undefined" && q != null);

    if (this.option.selectedPictureGallery &&
      this._gallery.sort() !== uniquePictures.sort()) {
      this._gallery = [];

      for (let image of uniquePictures) {
        if (!image) {
          continue;
        }
        if (image && !image.includes("/assets/images")) {
          if (!image.includes("http")) {
            image = this.globals.engine.formatPictureUrl(image);
          }

          // Don't add the same image twice (for multiple style scenarios)
          this._gallery.push(image);
        }
      }

      // Show custom photos from Media Associations
      if (this.globals?.mediaAssociations?.length && this.option?.selectedOption) {
        const matches = this.globals.mediaAssociations.filter(q => q.optionId === this.option.selectedOption.optionId);

        if (matches?.length) {
          for (const match of matches) {
            this._gallery.push(match.contentUrl);
          }
        }
      }
    }
    return this._gallery;
  }

  viewOption() {
    this.optionsSelected.emit(this.option);
  }

  toggleTrash(isAdded: true) {
    this.option.isAdded = !this.option.isAdded;
    event.stopPropagation();
  }

  setDefaultImage() {
    this.option.pictureUrl = "/assets/image-unavailable.png";
  }

  constructor(public globals: Globals,
    private areasService: AreasService,
    private router: Router,
    public dialog: MatDialog,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer) {
    this.globals.hideHeader = false;
    this.globals.validateSession();
  }

  get showTrashIcon(): boolean {
    // If an option is not included, it can be removed
    if (!this.option.selectedOption.isIncludedFeature) {
      return true;
    }

    // If an option is included but a selected style exists, it can be removed
    if (this.option.selectedOption.isIncludedFeature && this.hasRemovableStyle(this.option.selectedOption)) {
      return true;
    }

    return false;

    //if (!this.option.childOptions && this.option.isIncludedFeature || /*&& !this.hasRemovableStyle(this.option.selectedOption)*/) {
    //  return false;
    //}
    //else if (this.option.childOptions
    //  && this.option.selectedOption?.optionId === this.option.includedFeatureOptionId /*&& !this.hasRemovableStyle(this.option.selectedOption)*/) {
    //  return false;
    //} else {
    //  return true;
    //}
  }

  hasRemovableStyle(option: Option) {
    let result = false;
    if (this.option.selectedOption.styleSelections && this.option.selectedOption.styleSelections.length) {
      for (const colorList of this.option.selectedOption.styleSelections) {
        if (colorList.styleChoices.length > 1 && colorList.styleChoices.filter(x => x.isSelected).length && !(colorList.styleChoices.find(x => x.isSelected) == colorList.styleChoices.find(x => x.isDefault))) {
          result = true
        }
      }
    }
    return result;
  }

  get hasChoice(): boolean {
    return this.countEnhancements() + this.includedCount() > 1;
  }

  countEnhancements() {
    if (!(this.option.childOptions && this.option.childOptions.length)) return 0;

    let count = this.option.childOptions.filter(x => x.price > 0).length + this.countStyle();

    //if (this.option.isRequired && this.option.selectedOption) {
    //  if (this.option.selectedOption.price) {
    //    count--;
    //  }
    //}
    //else {
    //  count--;
    //}
    //if (!this.option.selectedOption) {
    //  count++;
    //}

    return count;
  }

  includedCount() {
    if (!(this.option.childOptions && this.option.childOptions.length)) return 0;
    let count = this.option.childOptions.filter(x => x.price === 0).length;
    return count;
  }

  toggleIncludedOptions() {
    const toggle = !this.isIncludedLiked()
    this.option.childOptions.filter(x => x.price === 0).forEach(option => {
      option.isLiked = toggle;
    })
  }

  isIncludedLiked(): boolean {
    //if (this.option.selectedOption
    //  && this.option.selectedOption.isIncludedFeature
    //  && this.option.selectedOption.isLiked) {
    //  return true;
    //}
    if (this.option.childOptions) {
      for (const child of this.option.childOptions) {
        if (child.price == 0 && !child.isLiked) {
          return false;
        }
      }
    }
    return true;
  }

  isOptionalLiked(): boolean {
    //if (this.option.selectedOption
    //  && !this.option.selectedOption.isIncludedFeature
    //  && this.option.selectedOption.isLiked) {
    //  return true;
    //}
    if (this.option.childOptions) {
      for (const child of this.option.childOptions) {
        if (child.price > 0 && !child.isLiked) {
          return false;
        }
      }
    }
    return true;
  }

  countStyle() {
    let styleCount = 0;
    if (!(this.option.childOptions && this.option.childOptions.length)) return 0;

    this.option.childOptions?.forEach(child => {
      if (child.styleSelections && child.styleSelections.length > 0) {
        child.styleSelections.forEach(style => {
          if (!style.description.includes("Style List")) {
            styleCount = styleCount + style.styleChoices.filter(choice => !choice.isRemoved).length;
          }
        });
      }
    });
    return styleCount;
  }

  checkOptionCount() {
    const count = this.option.styleSelections[0].styleChoices.filter(x => !x.isRemoved).length - 3;
    if (count > 0) {
      return count;
    }
    return "";
  }

  checkForSelected(option: Option) {
    option.isStyleSelected = true;
    //Check if included option have any default selection or not.
    if (option.isRequired && option.selectedOption.optionId === option.optionId && option.childOptions) {
      const selectedOption = option.childOptions.find(x => x.isSelected);
      if (!selectedOption) {
        option.isStyleSelected = false;
        return;
      }
    }
    //Check if included option have any style selection or not.
    this.includeOptions = option.childOptions?.filter(x => x.price == 0);
    if (option.selectedOption) {
      if (option.selectedOption.styleSelections) {
        for (const style of option.selectedOption.styleSelections) {
          for (const choice of style.styleChoices) {
            if (choice.isSelected) {
              option.isStyleSelected = true;
              break;
            }
            else {
              option.isStyleSelected = false;
            }
          }
        }
      }
    }
    this.subDescription = "";
    this.isOptionSelected = false;

    if (option.isSelected && !option.childOptions) {
      option.selectedOption = option;
      this.isOptionSelected = true;
      this.subDescription = this.selectedOptionAndStyleDescription(option);
      return true;
    }

    if (option.childOptions && option.childOptions.length > 1) {
      const isSelected = option.childOptions.find(x => x.isSelected);
      if (isSelected) {
        option.selectedOption = option.childOptions.find(x => x.isSelected || x.isAdded);
        this.subDescription = this.selectedOptionAndStyleDescription(option);
        this.isOptionSelected = true;
        return true;
      }
    }
    else {
      if (option.isAdded) {
        this.isOptionSelected = true;
        return true;
      }
    }
  }

  selectedOptionAndStyleDescription(option: Option) {
    let subDescription: string;
    this.isStyleSelected = false;
    if (option.selectedOption.isSelected || option.selectedOption.isAdded) {
      if (option.selectedOption.styleSelections && option.selectedOption.styleSelections.length > 0) {
        for (const style of option.selectedOption.styleSelections) {
          for (const choice of style.styleChoices) {
            if (choice.isSelected) {
              this.isStyleSelected = true;
              if (!subDescription) {
                subDescription = option.selectedOption.description + " : " + choice.description;
              }
              else {
                subDescription += ", " + choice.description;
              }
            }
          }
        }
        if (!subDescription && option.childOptions) {
          subDescription = option.selectedOption.description;
        }
        return subDescription;
      }
      else {
        if (option.childOptions) {
          return subDescription = option.selectedOption?.description;
        }
        return subDescription;
      }
    }
  }
  checkAreasStatus() {
    this.filteredAreas.forEach(area => {
      area.status = this.globals.engine.status[0].status;
      if (this.selectedArea?.id !== area.id) {
        area.status = this.globals.engine.status[1].status;
      }
      const includedOptions = this.globals.engine.optionsByArea(area.id, this.globals.areas, null);
      if (includedOptions.length > 0) {
        includedOptions.forEach(option => {
          this.checkForSelected(option);
        });
      }
      //else {
      //  if (this.selectedArea?.id !== area.id) {
      //    area.status = this.globals.engine.status[1].status;
      //  }
      //}
      const optionalOptions = this.globals.engine.optionalOptionsByArea(area.id, this.globals.areas, null);
      if (optionalOptions.length > 0) {
        optionalOptions.forEach(option => {
          this.checkForSelected(option);
        });
      }

      const selectedOptions = includedOptions.filter(x => x.selectedOption?.price > 0).length + optionalOptions.filter(x => x.selectedOption?.price > 0).length;

      // if (selectedOptions > 0) {
      area.status = this.globals.engine.status[0].status;
      const notCompletedIncluded = includedOptions.filter(x => !x.isStyleSelected);
      const notCompletedOptional = optionalOptions.filter(x => !x.isStyleSelected && x.selectedOption?.styleSelections);
      if (notCompletedIncluded.length + notCompletedOptional.length === 0) {
        area.status = this.globals.engine.status[1].status;
      }
      //}
      if (this.selectedArea?.id === area.id) {
        area.status = this.globals.engine.status[0].status;
      }
    });
    this.checkTagsStatus();
  }

  checkTagsStatus() {
    this.displayedTags.forEach(tag => {
      tag.status = this.globals.engine.status[0].status;
      if (this.selectedTag.name !== tag.name) {
        tag.status = this.globals.engine.status[1].status;
      }
      const includedOptions = this.globals.engine.optionsByArea(this.selectedArea.id, this.globals.areas, tag.name);
      if (includedOptions.length > 0) {
        includedOptions.forEach(option => {
          this.checkForSelected(option);
        });
      }
      //else {
      //  if (this.selectedTag.name !== tag.name) {
      //    tag.status =this.globals.engine.status[1].status;
      //  }
      //}
      const optionalOptions = this.globals.engine.optionalOptionsByArea(this.selectedArea.id, this.globals.areas, tag.name);
      if (optionalOptions.length > 0) {
        optionalOptions.forEach(option => {
          this.checkForSelected(option);
        });
      }
      // const selectedOptions = includedOptions.filter(x => x.selectedOption?.price > 0).length + optionalOptions.filter(x => x.selectedOption?.price > 0).length;

      // if (selectedOptions > 0) {
      tag.status = this.globals.engine.status[0].status;
      const notCompletedIncluded = includedOptions.filter(x => !x.isStyleSelected);
      const notCompletedOptional = optionalOptions.filter(x => !x.isStyleSelected && x.selectedOption?.styleSelections);

      if (notCompletedIncluded.length + notCompletedOptional.length === 0) {
        tag.status = this.globals.engine.status[1].status;
      }
      // }
      if (this.selectedTag.name === tag.name) {
        tag.status = this.globals.engine.status[0].status;
      }
    });
  }

  //get optionalOptionsAreLiked(): boolean {
  //  if (!this.option.childOptions) {
  //    return false;
  //  }

  //  if (!this.option.childOptions.filter(q => q.isLiked && q.price > 0).length) {
  //    return false;
  //  }

  //  return true;

  //}

  get getSelectionAlignment(): string {
    if (!this.option.selectedOption || this.option.selectedOption.price === 0) {
      return "4px";
    }
    return "50px";
  }

  removeOption(option: Option, event) {
    if (option.isRequired || (option.childOptions && option.childOptions.length)) {
      this.globals.engine.resetToIncludedFeature(option)
    } else {
      this.globals.engine.addRemoveOptionalOption(option, event)
    }
  }

  getSelectedOptionPictures(option: Option): string[] {
    const imgs: string[] = [];

    if (option?.selectedOption?.styleSelections) {
      for (const styleSelection of option.selectedOption.styleSelections) {
        const selectedChoices = styleSelection.styleChoices.filter(q => q.isSelected);
        for (const choice of selectedChoices) {
          imgs.push(choice.pictureUrl)
        }
      }
    }

    return imgs;
  }
}
